import { useEffect, useState } from 'react';
import { devopsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { FolderKanban, ArrowLeft, ExternalLink, AlertCircle } from 'lucide-react';

export default function DevOpsPage() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [workItems, setWorkItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingWorkItems, setLoadingWorkItems] = useState(false);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await devopsAPI.getProjects();
        setProjects(res.data);
      } catch (error) {
        console.error('Failed to fetch DevOps projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const handleSelectProject = async (project) => {
    setSelectedProject(project);
    setLoadingWorkItems(true);
    try {
      const res = await devopsAPI.getWorkItems(project.name, 100);
      setWorkItems(res.data);
    } catch (error) {
      toast.error('Failed to fetch work items');
    } finally {
      setLoadingWorkItems(false);
    }
  };

  const getStateColor = (state) => {
    const colors = {
      'New': 'bg-blue-100 text-blue-800',
      'Active': 'bg-yellow-100 text-yellow-800',
      'Resolved': 'bg-green-100 text-green-800',
      'Closed': 'bg-gray-100 text-gray-800',
      'Removed': 'bg-red-100 text-red-800',
    };
    return colors[state] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  if (selectedProject) {
    return (
      <div data-testid="devops-workitems-page" className="space-y-6 animate-fadeIn">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => setSelectedProject(null)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold font-['Manrope']">{selectedProject.name}</h1>
            <p className="text-muted-foreground mt-1">Work Items</p>
          </div>
        </div>

        <Card>
          <CardContent className="p-0">
            {loadingWorkItems ? (
              <div className="p-8 text-center">
                <Skeleton className="h-8 w-48 mx-auto mb-4" />
                <Skeleton className="h-4 w-64 mx-auto" />
              </div>
            ) : workItems.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <AlertCircle className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                <p>No work items found</p>
                <p className="text-sm mt-1">This project may not have any work items or there was an issue fetching them.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>State</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workItems.map((item) => (
                    <TableRow key={item.id} data-testid={`workitem-row-${item.id}`}>
                      <TableCell>
                        <code className="text-xs bg-muted px-2 py-1 rounded">#{item.id}</code>
                      </TableCell>
                      <TableCell className="font-medium max-w-md truncate">{item.title}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{item.work_item_type}</Badge>
                      </TableCell>
                      <TableCell>{item.assigned_to || '-'}</TableCell>
                      <TableCell>
                        <Badge className={getStateColor(item.state)}>{item.state}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="devops-page" className="space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">Azure DevOps</h1>
        <p className="text-muted-foreground mt-1">View projects and work items from Azure DevOps</p>
      </div>

      {projects.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FolderKanban className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-semibold mb-2">No Projects Found</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Unable to fetch projects from Azure DevOps. Please check that the PAT token is correctly configured and has the necessary permissions.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project) => (
            <Card
              key={project.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleSelectProject(project)}
              data-testid={`project-card-${project.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <FolderKanban className="w-5 h-5 text-primary" />
                  </div>
                  <Badge variant="outline">{project.state}</Badge>
                </div>
                <CardTitle className="text-lg mt-3">{project.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {project.description || 'No description available'}
                </p>
                <Button
                  variant="link"
                  className="p-0 h-auto mt-3 text-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(project.url, '_blank');
                  }}
                >
                  Open in Azure DevOps
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
